import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, ExternalLink, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

const Blog = () => {
  const { data: posts, isLoading, refetch } = useQuery({
    queryKey: ['blog-posts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(3);
      
      if (error) throw error;
      return data;
    }
  });

  // Atualização automática diária
  useEffect(() => {
    const checkAndUpdateNews = async () => {
      const lastUpdate = localStorage.getItem('last_news_update');
      const now = new Date().getTime();
      const oneDayMs = 24 * 60 * 60 * 1000;

      // Se nunca atualizou ou passou 24h desde a última atualização
      if (!lastUpdate || now - parseInt(lastUpdate) > oneDayMs) {
        try {
          await supabase.functions.invoke('fetch-tech-news');
          localStorage.setItem('last_news_update', now.toString());
          await refetch();
        } catch (error) {
          console.error('Error auto-updating news:', error);
        }
      }
    };

    checkAndUpdateNews();
  }, [refetch]);

  return (
    <section id="blog" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Blog & <span className="text-primary">Notícias</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Insights, novidades e tendências do mundo da tecnologia
          </p>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <RefreshCw className="animate-spin mx-auto mb-4" size={32} />
            <p className="text-muted-foreground">Carregando notícias...</p>
          </div>
        ) : posts && posts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <Card 
                key={post.id}
                className="overflow-hidden group hover:shadow-xl transition-all duration-300 card-shadow hover:-translate-y-1"
              >
                <div className="relative h-48 overflow-hidden bg-muted">
                  <img 
                    src={post.image_url} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src = 'https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?w=800&q=80';
                      e.currentTarget.onerror = null;
                    }}
                  />
                </div>
                
                <div className="p-6">
                  <Badge variant="secondary" className="mb-3">{post.category}</Badge>
                  <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-muted-foreground mb-4 leading-relaxed">{post.excerpt}</p>
                  
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                    <div className="flex items-center gap-1">
                      <Calendar size={14} />
                      <span>{new Date(post.date).toLocaleDateString('pt-BR')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock size={14} />
                      <span>{post.read_time}</span>
                    </div>
                  </div>

                  <a 
                    href={post.source_url || '#'} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm hover:text-primary transition-colors"
                  >
                    Ler no site original
                    <ExternalLink className="ml-2" size={16} />
                  </a>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Nenhuma notícia disponível no momento.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Blog;
